import React, { createContext, useReducer, useContext, useState, useEffect, Dispatch, useCallback } from "react";
import { APIId, Link } from "../../types";

type ResetType = {
  type: "RESET";
}

type SetField = {
  type: "SET_FIELD";
  field: string;
  value: any;
}

type SetFields = {
  type: "SET_FIELDS";
  values: Partial<FlashcardSetupState>;
}

type SetSmartNotes = {
  type: "SET_SMART_NOTES";
  smartNotes: string[];
}

type SetSmartNotesLoading = {
  type: "SET_SMART_NOTES_LOADING";
  loading: boolean;
}

type SetUploading = {
  type: "SET_UPLOADING";
  uploading: boolean;
}

type NextStep = {
  type: "NEXT_STEP";
}

type PrevStep = {
  type: "PREV_STEP";
}

type FlashcardSetupAction = ResetType | SetField | SetFields | SetSmartNotes | SetSmartNotesLoading | SetUploading | NextStep | PrevStep;

interface FlashcardSetupState {
  numberOfCards: string;
  difficulty: string;
  adaptiveMix: boolean;
  skippingHints: boolean;
  files: string[]; // signed ids
  links: string[];
  selectedSmartNotes: string[];
  prompt: string;
  smartNotes: string[];
  smartNotesLoading: boolean;
  uploading: boolean;
  step: number;
  language: string;
}

interface FlashcardSetupContextType {
  state: FlashcardSetupState;
  dispatch: Dispatch<FlashcardSetupAction>;
  handleFieldChange: (fields: Partial<FlashcardSetupState>) => void;
  handleFilesChange: (files: string[]) => void;
  handleLinksChange: (links: Link[]) => void;
  handleSmartNoteSelect: (selected: APIId[]) => void;
  handlePromptChange: (prompt: string) => void;
  handleNextStep: () => void;
  handleSubmit: () => Promise<void>;
  intializedWithSmartNotes: boolean;
  smartNoteIdscope: APIId[];
  languages?: Record<string, string>;
  open: boolean;
}

const initialState: FlashcardSetupState = {
  numberOfCards: "",
  difficulty: "easy",
  adaptiveMix: false,
  skippingHints: false,
  files: [],
  links: [],
  selectedSmartNotes: [],
  prompt: "",
  smartNotes: [],
  smartNotesLoading: false,
  uploading: false,
  step: 1,
  language: "en",
};

function reducer(state: FlashcardSetupState, action: FlashcardSetupAction) {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field as keyof FlashcardSetupState]: action.value };
    case "SET_FIELDS":
      return { ...state, ...action.values };
    case "SET_SMART_NOTES":
      return { ...state, smartNotes: action.smartNotes, smartNotesLoading: false };
    case "SET_SMART_NOTES_LOADING":
      return { ...state, smartNotesLoading: action.loading };
    case "SET_UPLOADING":
      return { ...state, uploading: action.uploading };
    case "NEXT_STEP":
      return { ...state, step: state.step + 1 };
    case "PREV_STEP":
      return { ...state, step: state.step - 1 };
    case "RESET":
      return { ...initialState };
    default:
      return state;
  }
}

const FlashcardSetupContext = createContext<FlashcardSetupContextType | undefined>(undefined);

export interface FlashcardSetupProviderProps {
  children: React.ReactNode;
  smartNoteIdscope?: APIId[];
  initialState?: Partial<FlashcardSetupState>;
  mode?: string;
  folder_id?: string | number;
  languages?: Record<string, string>;
  open: boolean;
  onClose: () => void;
}

export function FlashcardSetupProvider({ children, smartNoteIdscope = [], initialState: initial = {}, mode = "setup", folder_id, languages, open, onClose }: FlashcardSetupProviderProps) {
  const [state, dispatch] = useReducer(reducer, { ...initialState, ...initial });

  useEffect(() => {
    if (!open) {
      dispatch({ type: "RESET" });
    }
    return () => {
      dispatch({ type: "RESET" });
    };
  }, [open]);

  const handleFieldChange = useCallback((fields: Partial<FlashcardSetupState>) => dispatch({ type: "SET_FIELDS", values: fields }), []);
  const handleFilesChange = useCallback((files: string[]) => dispatch({ type: "SET_FIELD", field: "files", value: files }), []);
  const handleLinksChange = useCallback((links: Link[]) => dispatch({ type: "SET_FIELD", field: "links", value: links.map(link => link.url) }), []);
  const handleSmartNoteSelect = useCallback((selected: APIId[]) => dispatch({ type: "SET_FIELD", field: "selectedSmartNotes", value: Array.isArray(selected) ? selected : [selected] }), []);
  const handlePromptChange = useCallback((prompt: string) => dispatch({ type: "SET_FIELD", field: "prompt", value: prompt }), []);
  const handleNextStep = useCallback(() => dispatch({ type: "NEXT_STEP" }), []);

  const handleSubmit = async () => {
    dispatch({ type: "SET_UPLOADING", uploading: true });
    try {
      const hasPrompt = state.prompt && state.prompt.trim().length > 0;
      const hasMaterials = state.selectedSmartNotes.length > 0 || state.files.length > 0 || state.links.length > 0;
      const isPromptOnly = hasPrompt && !hasMaterials;

      const payload = {
        card_set: {
          adaptive_mix: state.adaptiveMix,
          difficulty: state.difficulty,
          files: state.files,
          links: state.links,
          prompt: state.prompt,
          quantity: state.numberOfCards,
          skipping_hints: state.skippingHints,
          selected_smart_notes: [...state.selectedSmartNotes, ...smartNoteIdscope],
          language: state.language
        },
        ...(folder_id && { folder_id: folder_id.toString() })
      }

      const options = {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify(payload),
      };

      const endpoint = isPromptOnly ? '/ai/prompt_card_sets' : '/ai/card_sets';
      const response = await fetch(endpoint, options);
      const data = await response.json();

      if (data.card_set_id) {
        const url = folder_id
          ? `/card_sets/${data.card_set_id}/slides?folder_id=${folder_id}`
          : `/card_sets/${data.card_set_id}/slides`;
        Turbo.visit(url);
      }

      onClose();
      dispatch({ type: "RESET" });
    } catch (e) {
      dispatch({ type: "SET_UPLOADING", uploading: false });
    }
  };

  const contextValue = {
    state,
    dispatch,
    open,
    handleFieldChange,
    handleFilesChange,
    handleLinksChange,
    handleSmartNoteSelect,
    handlePromptChange,
    handleNextStep,
    handleSubmit,
    intializedWithSmartNotes: smartNoteIdscope.length > 0,
    smartNoteIdscope,
    folder_id,
    languages,
  };

  return (
    <FlashcardSetupContext.Provider value={contextValue}>
      {children}
    </FlashcardSetupContext.Provider>
  );
}

export function useFlashcardSetup(): FlashcardSetupContextType {
  const context = useContext(FlashcardSetupContext);
  if (!context) throw new Error("useFlashcardSetup must be used within a FlashcardSetupProvider");
  return context;
}

export default FlashcardSetupContext;
